import { createContext, ReactNode, useEffect, useRef, useState } from 'react';

import { FileDownloadStatusEnum } from '../enums/FileDownloadStatusEnum';
import { StorageItemKeyEnum } from '../enums/StorageItemKeyEnum';
import { TransferService } from '../services/transferService';
import { getStorageItem, storeStorageItem } from '../utils/storage';
import { generateUUID } from '../utils/utils';
import { ApiErrorEnum } from '../enums/ApiErrorEnum';
import { NotificationTypeEnum } from '../enums/NotificationTypeEnum';
import { useNotifications } from '../hooks/useNotifications';
import { useTranslation } from 'react-i18next';

interface ProcessStatus {
  status: FileDownloadStatusEnum;
  progressPercentage: number;
  additionalData: string;
  processId: string;
  name?: string;
}

interface FileContextType {
  handleCancelProcess: (processId: string) => void;
  handleDownloadFiles: (ids: number[], fileName?: string) => void;
  handleCancelAllProcesses: () => void;
  processStatuses: ProcessStatus[];
  isLoading: boolean;
}

export const FileDownloadContext = createContext<FileContextType>({
  handleCancelProcess: () => {},
  handleDownloadFiles: () => {},
  handleCancelAllProcesses: () => {},
  isLoading: false,
  processStatuses: [],
});

interface FileProviderProps {
  children: ReactNode;
}

const transferService = new TransferService();

export const FileTransferProvider = ({ children }: FileProviderProps) => {
  const { createNotification } = useNotifications();
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // State to manage active process IDs
  const [activeProcessesIds, setActiveProcessesIds] = useState<string[]>([]);

  // State to manage statuses of all processes
  const [processStatuses, setProcessStatuses] = useState<ProcessStatus[]>([]);

  // Ref to store polling intervals for each processId
  const pollingIntervals = useRef<Map<string, number>>(new Map());

  /**
   * Extracts the file name from a given URL string.
   *
   * The function parses the URL and returns the last segment of the path.
   * If an error occurs during this process, it defaults to returning 'files.zip'.
   *
   * @param {string} url - The URL string from which to extract the file name.
   * @returns {string} The file name extracted from the URL, or 'files.zip' in case of an error.
   */
  const getFileNameFromUrl = (url: string): string => {
    try {
      const path = new URL(url).pathname;
      const segments = path.split('/');
      // Return the last segment of the path
      return segments[segments.length - 1];
    } catch (error) {
      return 'files.zip';
    }
  };

  // Load activeProcessesIds from storage on mount
  useEffect(() => {
    const loadActiveProcessesIds = async () => {
      const savedState = await getStorageItem(
        'localStorage',
        StorageItemKeyEnum.activeProcessesIds
      );
      if (savedState !== null) {
        setActiveProcessesIds(savedState);
      }
    };
    loadActiveProcessesIds();
  }, []);

  // Save activeProcessesIds to storage whenever it changes
  useEffect(() => {
    const saveActiveProcessesIds = async () => {
      await storeStorageItem(
        'localStorage',
        StorageItemKeyEnum.activeProcessesIds,
        activeProcessesIds
      );
    };
    saveActiveProcessesIds().then(() => {});
  }, [activeProcessesIds]);

  // Polling logic for activeProcessesIds
  useEffect(() => {
    // Start polling for new processIds
    activeProcessesIds.forEach((processId) => {
      if (!pollingIntervals.current.has(processId)) {
        const pollProcess = async () => {
          try {
            const response = await transferService.getProcessStatus(processId);

            // Update the process status in processStatuses
            setProcessStatuses((prevStatuses) => {
              const index = prevStatuses.findIndex(
                (status) => status.processId === processId
              );
              const updatedProcess: ProcessStatus = {
                ...response,
                processId,
              };

              if (index >= 0) {
                // Update existing process status
                const newStatuses = [...prevStatuses];
                newStatuses[index] = updatedProcess;
                return newStatuses;
              } else {
                // Add new process status
                return [...prevStatuses, updatedProcess];
              }
            });

            if (response.status !== FileDownloadStatusEnum.InProgress) {
              // Process is either Success or Failed

              // Stop polling for this processId
              clearInterval(pollingIntervals.current.get(processId));
              pollingIntervals.current.delete(processId);

              // Remove from activeProcessesIds
              setActiveProcessesIds((prevIds) =>
                prevIds.filter((id) => id !== processId)
              );

              if (
                response.status === FileDownloadStatusEnum.Success &&
                response.additionalData
              ) {
                const downloadUrl = response.additionalData;

                const fileName = getFileNameFromUrl(downloadUrl);

                // Set process name in processStatuses for this processId
                setProcessStatuses((prevStatuses) => {
                  const index = prevStatuses.findIndex(
                    (status) => status.processId === processId
                  );
                  const updatedProcess: ProcessStatus = {
                    ...response,
                    processId,
                    name: fileName,
                  };

                  if (index >= 0) {
                    // Update existing process status
                    const newStatuses = [...prevStatuses];
                    newStatuses[index] = updatedProcess;
                    return newStatuses;
                  } else {
                    // Add new process status
                    return [...prevStatuses, updatedProcess];
                  }
                });

                try {
                  // Extract the 'se' parameter from the URL
                  const url = new URL(downloadUrl);
                  const seParam = url.searchParams.get('se');

                  if (seParam) {
                    // Parse the expiration time in UTC
                    const expirationTime = new Date(seParam); // ISO 8601 string in UTC
                    const currentTime = new Date(); // Current local time

                    // Calculate the time until expiration in milliseconds
                    const timeUntilExpiration =
                      expirationTime.getTime() - currentTime.getTime();

                    if (timeUntilExpiration > 0) {
                      // Set a timeout to remove the process when the URL expires
                      setTimeout(() => {
                        setProcessStatuses((prevStatuses) =>
                          prevStatuses.filter(
                            (status) => status.processId !== processId
                          )
                        );
                      }, timeUntilExpiration);
                    } else {
                      // If already expired, remove immediately
                      setProcessStatuses((prevStatuses) =>
                        prevStatuses.filter(
                          (status) => status.processId !== processId
                        )
                      );
                    }
                  } else {
                    console.warn(
                      `'se' parameter not found in URL for process ${processId}`
                    );
                  }
                } catch (error) {
                  console.error(
                    `Invalid URL in additionalData for process ${processId}:`,
                    error
                  );
                }

                // Initiate download
                const link = document.createElement('a');
                link.href = downloadUrl;
                link.setAttribute('download', '');
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
              }
            }
            // If status is InProgress, continue polling
          } catch (error) {
            console.error(
              `Error getting status for processId ${processId}:`,
              error
            );

            // Stop polling on error
            clearInterval(pollingIntervals.current.get(processId));
            pollingIntervals.current.delete(processId);

            // Remove from activeProcessesIds
            setActiveProcessesIds((prevIds) =>
              prevIds.filter((id) => id !== processId)
            );

            // Update the process status to Failed in processStatuses
            setProcessStatuses((prevStatuses) => {
              const index = prevStatuses.findIndex(
                (status) => status.processId === processId
              );
              const failedProcess: ProcessStatus = {
                additionalData: '',
                processId,
                progressPercentage: 0,
                status: FileDownloadStatusEnum.Failed,
              };

              if (index >= 0) {
                // Update existing process status
                const newStatuses = [...prevStatuses];
                newStatuses[index] = failedProcess;
                return newStatuses;
              } else {
                // Add new process status
                return [...prevStatuses, failedProcess];
              }
            });
          }
        };

        // Start initial poll immediately
        pollProcess();

        // Set up polling interval
        const intervalId = window.setInterval(pollProcess, 2500);
        pollingIntervals.current.set(processId, intervalId);
      }
    });

    // Stop polling for processIds no longer in activeProcessesIds
    pollingIntervals.current.forEach((intervalId, processId) => {
      if (!activeProcessesIds.includes(processId)) {
        clearInterval(intervalId);
        pollingIntervals.current.delete(processId);
      }
    });

    // Cleanup intervals on component unmount
    return () => {
      pollingIntervals.current.forEach((intervalId) => {
        clearInterval(intervalId);
      });
      pollingIntervals.current.clear();
    };
  }, [activeProcessesIds]);

  /**
   * Handles the download initiation and adds the processId to activeProcessesIds
   */
  const handleDownloadFiles = async (
    ids: number[],
    fileName?: string
  ): Promise<void> => {
    setIsLoading(true);

    const fakeProcessId = generateUUID();
    setProcessStatuses((prevStatuses) => [
      ...prevStatuses,
      {
        additionalData: '',
        processId: fakeProcessId,
        progressPercentage: 0,
        status:
          ids.length > 1
            ? FileDownloadStatusEnum.InProgress
            : FileDownloadStatusEnum.Pending,
        name: fileName,
      },
    ]);

    try {
      const response = await transferService
        .download({ filesId: ids })
        .catch((error) => {
          switch (error.response) {
            case ApiErrorEnum.AntiCaptureActivated:
              createNotification(
                t('errors.antiCaptureActivated'),
                NotificationTypeEnum.DANGER
              );
              break;
            case ApiErrorEnum.DownloadRestrictionActivated:
              createNotification(
                t('errors.downloadRestrictionActivated'),
                NotificationTypeEnum.DANGER
              );
              break;
          }
          throw error;
        });

      // Controlla se è un oggetto Blob o un JSON
      if (response instanceof Response && response.headers) {
        const contentType = response.headers.get('Content-Type');

        // Gestisce il caso di un singolo file che restituisce un Blob
        if (contentType && contentType.includes('application/octet-stream')) {
          const blob = await response.blob();

          // Genera un link per scaricare il file
          const downloadUrl = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = downloadUrl;

          // Imposta il nome del file (opzionale)
          const contentDisposition = response.headers.get(
            'Content-Disposition'
          );
          if (contentDisposition) {
            const match = contentDisposition.match(/filename="?([^"]+)"?/);
            link.download = match ? match[1] : fileName || 'downloaded-file';
          } else {
            link.download = fileName || 'downloaded-file';
          }

          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);

          // Rilascia l'URL del Blob
          window.URL.revokeObjectURL(downloadUrl);

          // Push a fake process status to indicate completion
          setProcessStatuses((prevStatuses) =>
            prevStatuses.map((status) =>
              status.processId === fakeProcessId
                ? {
                    ...status,
                    status: FileDownloadStatusEnum.Success,
                    progressPercentage: 100,
                  }
                : status
            )
          );
          return; // Exit from the function because the download is complete
        }
      }

      // Se la risposta è un JSON, gestisci il `processId`
      const realProcessId = response.processId;
      if (realProcessId) {
        // Rimpiazziamo il fakeProcessId con quello reale e avviamo il polling
        setActiveProcessesIds((prevIds) => [...prevIds, realProcessId]);
        setProcessStatuses((prevStatuses) =>
          prevStatuses.map((status) =>
            status.processId === fakeProcessId
              ? {
                  ...status,
                  processId: realProcessId,
                }
              : status
          )
        );
      }
    } catch (error) {
      console.error('Catch error', error);
      setProcessStatuses((prevStatuses) =>
        prevStatuses.map((status) =>
          status.processId === fakeProcessId
            ? {
                ...status,
                status: FileDownloadStatusEnum.Failed,
                progressPercentage: 0,
              }
            : status
        )
      );
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Asynchronously handles the cancellation of a process.
   *
   * @param {string} processId - The unique identifier of the process to be cancelled.
   * @returns {Promise<void>} A promise that resolves when the process has been successfully cancelled.
   *
   * This function performs the following operations:
   * 1. Removes the processId from the list of active process IDs.
   * 2. Updates the status of the process to 'Failed' in the process statuses.
   * 3. Logs any errors encountered during the cancellation process.
   */
  const handleCancelProcess = async (processId: string): Promise<void> => {
    try {
      // Remove the processId from activeProcessesIds
      setActiveProcessesIds((prevIds) =>
        prevIds.filter((id) => id !== processId)
      );
      // Update the process status to Failed in processStatuses
      setProcessStatuses((prevStatuses) => {
        const index = prevStatuses.findIndex(
          (status) => status.processId === processId
        );
        const failedProcess: ProcessStatus = {
          additionalData: '',
          processId,
          progressPercentage: 0,
          status: FileDownloadStatusEnum.Failed,
        };

        if (index >= 0) {
          // Update existing process status
          const newStatuses = [...prevStatuses];
          newStatuses[index] = failedProcess;
          return newStatuses;
        } else {
          // Add new process status
          return [...prevStatuses, failedProcess];
        }
      });
    } catch (error) {
      console.error(`Error cancelling process ${processId}:`, error);
    }
  };

  /**
   * Cancels all active processes by removing all process IDs from the active
   * processes list and clearing the process statuses. This function updates
   * the state of activeProcessesIds and processStatuses to empty arrays.
   *
   * @returns {Promise<void>} A promise that resolves when the cancellation
   *    and state updates are complete.
   */
  const handleCancelAllProcesses = async (): Promise<void> => {
    // Remove all processIds from activeProcessesIds and from processStatuses
    setActiveProcessesIds([]);
    setProcessStatuses([]);
  };

  return (
    <FileDownloadContext.Provider
      value={{
        handleCancelProcess,
        handleDownloadFiles,
        handleCancelAllProcesses,
        isLoading,
        processStatuses,
      }}
    >
      {children}
    </FileDownloadContext.Provider>
  );
};
